import { MAX_CANVAS_DIMENSION, MAX_CANVAS_PIXELS } from './config';
import { calculateLockedResolution, simplifyAspectRatio } from './ratioGuard';
import type { ProjectionSettings, Resolution } from './types';

export type ProjectionResolutionChange =
  | { kind: 'width'; value: number }
  | { kind: 'height'; value: number }
  | { kind: 'preset'; resolution: Resolution };

export function updateProjectionResolution(
  projection: ProjectionSettings,
  change: ProjectionResolutionChange,
): ProjectionSettings {
  const previousResolution = projection.resolution;
  let resolution: Resolution;
  let resolutionAspectRatio = projection.resolutionAspectRatio;

  if (change.kind === 'preset') {
    resolution = { ...change.resolution };
    resolutionAspectRatio = simplifyAspectRatio(resolution);
  } else if (projection.resolutionAspectLocked) {
    resolution = calculateConstrainedLockedResolution(
      change.kind,
      change.value,
      resolutionAspectRatio,
    );
  } else {
    resolution = {
      ...previousResolution,
      [change.kind]: constrainUnlockedDimension(change.kind, change.value, previousResolution),
    };
    resolutionAspectRatio = simplifyAspectRatio(resolution);
  }

  const physical = projection.physicalFollowsResolution
    ? {
        widthMm: scalePhysicalDimension(
          projection.physical.widthMm,
          resolution.width,
          previousResolution.width,
        ),
        heightMm: scalePhysicalDimension(
          projection.physical.heightMm,
          resolution.height,
          previousResolution.height,
        ),
      }
    : projection.physical;

  return { ...projection, resolution, resolutionAspectRatio, physical };
}

export function toggleProjectionAspectLock(projection: ProjectionSettings): ProjectionSettings {
  const resolutionAspectLocked = !projection.resolutionAspectLocked;
  return {
    ...projection,
    resolutionAspectLocked,
    resolutionAspectRatio: resolutionAspectLocked
      ? simplifyAspectRatio(projection.resolution)
      : projection.resolutionAspectRatio,
  };
}

export function getMaximumProjectionDimension(
  projection: ProjectionSettings,
  dimension: 'width' | 'height',
): number {
  if (projection.resolutionAspectLocked) {
    return maximumLockedDimension(projection.resolutionAspectRatio, dimension);
  }
  const otherDimension =
    dimension === 'width' ? projection.resolution.height : projection.resolution.width;
  return Math.min(MAX_CANVAS_DIMENSION, Math.floor(MAX_CANVAS_PIXELS / otherDimension));
}

function calculateConstrainedLockedResolution(
  leading: 'width' | 'height',
  requestedValue: number,
  aspect: Resolution,
): Resolution {
  const primaryMaximum = maximumLockedDimension(aspect, leading);
  let primary = Math.max(1, Math.min(Math.round(requestedValue), primaryMaximum));
  let resolution = calculateLockedResolution(leading, primary, aspect);

  while (!fitsCanvasLimits(resolution) && primary > 1) {
    primary -= 1;
    resolution = calculateLockedResolution(leading, primary, aspect);
  }
  return resolution;
}

function constrainUnlockedDimension(
  dimension: 'width' | 'height',
  requestedValue: number,
  resolution: Resolution,
): number {
  const otherDimension = dimension === 'width' ? resolution.height : resolution.width;
  const maximum = Math.min(MAX_CANVAS_DIMENSION, Math.floor(MAX_CANVAS_PIXELS / otherDimension));
  return Math.max(1, Math.min(Math.round(requestedValue), maximum));
}

function maximumLockedDimension(aspect: Resolution, dimension: 'width' | 'height'): number {
  return dimension === 'width'
    ? Math.min(
        MAX_CANVAS_DIMENSION,
        Math.floor((MAX_CANVAS_DIMENSION * aspect.width) / aspect.height),
        Math.floor(Math.sqrt((MAX_CANVAS_PIXELS * aspect.width) / aspect.height)),
      )
    : Math.min(
        MAX_CANVAS_DIMENSION,
        Math.floor((MAX_CANVAS_DIMENSION * aspect.height) / aspect.width),
        Math.floor(Math.sqrt((MAX_CANVAS_PIXELS * aspect.height) / aspect.width)),
      );
}

function scalePhysicalDimension(value: number, nextPixels: number, previousPixels: number): number {
  const scaled = value * (nextPixels / previousPixels);
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(Number.EPSILON, roundToSixDecimals(scaled)));
}

function roundToSixDecimals(value: number): number {
  return Math.round((value + Number.EPSILON) * 1_000_000) / 1_000_000;
}

function fitsCanvasLimits(resolution: Resolution): boolean {
  return (
    resolution.width <= MAX_CANVAS_DIMENSION &&
    resolution.height <= MAX_CANVAS_DIMENSION &&
    resolution.width * resolution.height <= MAX_CANVAS_PIXELS
  );
}
